// Smithing table (M59). PURE (ENG-1): matches a template + base + addition against
// the `smithing` recipes and produces the upgraded stack. The host supplies the
// recipes and the item names; it consumes the inputs and places the result.
//
// Netherite is the reason this exists. It is the one material that is never
// crafted — you take a finished diamond piece and re-forge it, which is why the
// upgrade has to CARRY the item forward rather than mint a fresh one: the
// enchantments you spent levels on, the name you paid an anvil for, and the wear
// you already put into it all survive the trip (Minecraft's rule).
import { type ItemStack, type Inventory, emptyStack } from './inventory';

/** A `smithing` recipe as the table sees it (the registry's shape, by name). */
export interface SmithingRecipe {
  name: string;
  /** Required template item; absent ⇒ the recipe takes no template. */
  template?: string;
  base: string;
  addition: string;
  result: string;
}

/** What the three input slots currently hold (undefined = empty). */
export interface SmithingInputs {
  template?: string;
  base?: string;
  addition?: string;
}

/**
 * The recipe these three inputs satisfy, or null. A recipe that declares a
 * template REQUIRES one — an empty template slot is not a wildcard, or the
 * netherite upgrade would cost nothing but the ingot.
 */
export function matchSmithing(
  recipes: readonly SmithingRecipe[],
  inputs: SmithingInputs,
): SmithingRecipe | null {
  for (const r of recipes) {
    if (r.base !== inputs.base || r.addition !== inputs.addition) continue;
    if ((r.template ?? undefined) !== (inputs.template ?? undefined)) continue;
    return r;
  }
  return null;
}

/** Which items a smithing table will accept in each slot (for slot filtering). */
export function smithingSlotItems(recipes: readonly SmithingRecipe[]): {
  templates: Set<string>; bases: Set<string>; additions: Set<string>;
} {
  const templates = new Set<string>(), bases = new Set<string>(), additions = new Set<string>();
  for (const r of recipes) {
    if (r.template) templates.add(r.template);
    bases.add(r.base);
    additions.add(r.addition);
  }
  return { templates, bases, additions };
}

/**
 * Forge the upgraded stack: the result item, wearing the base's components.
 *
 * Wear carries over as a RAW damage value, not a fraction — Minecraft's own
 * behaviour, and the reason a battered diamond pickaxe comes back off the table
 * with more life left in it than it went on with (netherite's bar is longer).
 * `resultDurability` only clamps: a base that was somehow damaged past the new
 * maximum comes back at one hit from breaking rather than already broken.
 */
export function applySmithing(base: ItemStack, resultItem: number, resultDurability?: number): ItemStack {
  const c = base.components;
  if (!c) return { item: resultItem, count: 1 };
  const carried = JSON.parse(JSON.stringify(c)) as typeof c;
  if (carried.damage !== undefined && resultDurability !== undefined) {
    carried.damage = Math.max(0, Math.min(carried.damage, resultDurability - 1));
  }
  return { item: resultItem, count: 1, components: carried };
}

// ── Host-facing: forge the item in hand ──────────────────────────────────────
/** Item-name ↔ id lookup the table needs (the registry supplies it). */
export interface SmithingItems {
  idOf: ReadonlyMap<string, number>;
  nameOf: readonly string[];
  /** Max durability of an item, for the wear clamp; absent ⇒ not a tool. */
  durabilityOf(name: string): number | undefined;
}

/** Remove `n` of an item id from the inventory, front to back. */
function removeCount(inv: Inventory, id: number, n: number): void {
  let left = n;
  for (let i = 0; i < inv.size && left > 0; i++) {
    const s = inv.get(i);
    if (s.item !== id || s.count <= 0) continue;
    const take = Math.min(left, s.count);
    left -= take;
    inv.set(i, s.count - take <= 0 ? emptyStack() : { ...s, count: s.count - take });
  }
}

/** The upgrade the held item is eligible for, given what else the player carries. */
export function heldUpgrade(
  items: SmithingItems,
  inv: Inventory,
  recipes: readonly SmithingRecipe[],
  heldSlot: number,
): { recipe: SmithingRecipe; affordable: boolean } | null {
  const held = inv.get(heldSlot);
  const baseName = held.count > 0 ? items.nameOf[held.item] : undefined;
  if (baseName === undefined) return null;
  const recipe = recipes.find((r) => r.base === baseName);
  if (!recipe) return null;
  const has = (name: string | undefined): boolean => {
    if (name === undefined) return true;
    const id = items.idOf.get(name);
    return id !== undefined && inv.countOf(id) >= 1;
  };
  return { recipe, affordable: has(recipe.template) && has(recipe.addition) };
}

/**
 * Re-forge the held item: consume one template + one addition from anywhere in
 * the inventory and swap the held stack for the upgrade. Returns the recipe that
 * fired, or null when nothing was possible (and nothing was consumed).
 *
 * Only ONE piece is upgraded per press even if the held stack somehow holds more
 * — every smithable item is maxStack 1, and consuming a whole stack's worth of
 * ingots for one result would be a quiet theft.
 */
export function forgeHeld(
  items: SmithingItems,
  inv: Inventory,
  recipes: readonly SmithingRecipe[],
  heldSlot: number,
): SmithingRecipe | null {
  const match = heldUpgrade(items, inv, recipes, heldSlot);
  if (!match || !match.affordable) return null;
  const resultId = items.idOf.get(match.recipe.result);
  if (resultId === undefined) return null;
  if (match.recipe.template) removeCount(inv, items.idOf.get(match.recipe.template)!, 1);
  removeCount(inv, items.idOf.get(match.recipe.addition)!, 1);
  const held = inv.get(heldSlot);
  inv.set(heldSlot, applySmithing(held, resultId, items.durabilityOf(match.recipe.result)));
  return match.recipe;
}
